import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";

import axiosInstance from "@/api/config";
import { getDatasetMetadata } from "@/api/dataset/datasetApi";
import { usePostDatasetAnalysis } from "@/api/dataset/useDatasetAPI";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import { CirclePlusIcon, FilePlusIcon, FolderOpenIcon } from "@/assets/icons";
import DatasetList from "@/components/dataset/datasetList/index";
import SpatialAreaInput from "@/components/spatialAnalysis/spatialAreaInput";
import SpatialOutputAnalysis from "@/components/spatialAnalysis/spatialOutputAnalysis";
import { buildCoverageTypePayload } from "@/components/spatialAnalysis/spatialOutputAnalysis/utils";
import { Divider } from "@/components/ui/Divider";
import LimitWarning from "@/components/ui/LimitWarning";
import { Timezone } from "@/constants/dateTime";
import { MESSAGE } from "@/constants/message";
import {
  getDeepestAdminFilterFirst,
  hasSpatialAggregationChanged
} from "@/helpers/datasetAttributeHelper";
import { buildFilterAsDatasetPayload } from "@/helpers/filterHelper";
import { removeStartFromUnderscore } from "@/helpers/string";
import { useMap } from "@/hooks/map/core/useMapContext";
import { useAdmAreaMappingCheck } from "@/hooks/map/useAdmAreaMappingCheck";
import {
  EModuleName,
  EModulePermission,
  useGetUserPermission
} from "@/hooks/permission/useGetUserPermission";
import { useConfiguration } from "@/hooks/project/useConfiguration";
import useConfigurationMappers from "@/hooks/project/useConfigurationMapper";
import { useSpatialInputRowsLimit } from "@/hooks/spatialAnalysis/useSpatialInputRowsLimit";
import { useValidationAnalysisConfigInput } from "@/hooks/spatialAnalysis/useValidationAnalysisConfig";
import { useDatadogAnalytics } from "@/hooks/tracker/useDatadogAnalytics";
import { useConfirmationModal } from "@/hooks/ui/modal/useConfirmationModal";
import useHandleClearView from "@/hooks/ui/useHandleClearView";
import useToast, { EToastType } from "@/hooks/ui/useToast";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { queryClient } from "@/lib/react-query";
import { cn } from "@/lib/utils";
import { RootState } from "@/store";
import {
  selectIsWeightAutoAdjusted,
  selectWeightAdjustedDatasetIds
} from "@/store/dataset/selectors";
import {
  clearAllAdmAreaMappingUpdates,
  resetDatasetState,
  setDatasetHasUpdatedSpatialAggregation,
  setDatasetHistory,
  updateDatasetPublishedStatus
} from "@/store/slice/datasetSlice";
import { resetGlobalFilter } from "@/store/slice/globalFilterSlice";
import {
  resetSpatialState,
  setAdminAreaSettingsDataTemp,
  setAdminTreeSettingsTempSelectedIds,
  setCatchmentSettingsTemp,
  setCurrRunningAnalysisId,
  setLatestAnalysisId,
  setLocallyRunAnalysisId,
  setPolygonSettingsTemp,
  setSpatialState,
  spatialInitialState,
  SpatialState
} from "@/store/slice/spatialSlice";
import {
  resetUIDataConfigExceptSidebarMenu,
  setActiveSpatialAnalysisPanel,
  setShowPolygonPopup,
  toggleAreaAdminPopup,
  toggleCatchmentPopup,
  toggleGlobalFilterPopup,
  toggleShowProjectList
} from "@/store/slice/uiSlice";
import { resetWidgetState, setWidgetHistory } from "@/store/slice/widgetSlice";
import {
  AdminArea,
  AnalysisResultType,
  CustomCatchment,
  DatasetAttributeSchema,
  DatasetFilter,
  Marker,
  OutputGrid,
  OutputSiteProfiling,
  ProportionMethodOutput,
  SpatialAnalysisDatasetFilter
} from "@/types/dataset";
import { MarkerPinItem } from "@/types/map";
import { ProjectFormData } from "@/types/project";
import {
  AdminAreaSettings,
  AreaInputType,
  ECatchmentInputBy,
  ECatchmentType,
  EOutputAnalysisType,
  EOutputGridType,
  IsochroneData,
  ScoringOptions,
  ScoringOptionsEnum
} from "@/types/spatial";
import { createMarkersFromDataset } from "@/utils/catchment";
import { convertDatasetResponseToFeatureCollection } from "@/utils/dataset";
import {
  getDatasetAttributeScoringId,
  getPrimaryPoiAttribute
} from "@/utils/datasetAttributes";
import { getRadiusPolygon } from "@/utils/map";
import { parseFormattedNumber } from "@/utils/number";
import {
  getIsIncludedScoring,
  getNegativeImpactValue,
  getPercentageValue,
  parseWeightValue,
  parseZScore
} from "@/utils/weightValues";
import { geoJSONArrayToWKT, geoJSONToWKT } from "@/utils/wkt";

import { LokasiAIProvider } from "@lokasi-ai-web-sdk/app";
import { pointOnFeature } from "@turf/turf";
import { Button } from "equator-design-system";
import { twMerge } from "tailwind-merge";
import { v4 as uuidv4 } from "uuid";

import { UnlockPremiumModal } from "../dataset/datasetExplorer/UnlockPremiumModal";
import { GlobalFilter } from "../globalFilter";
import NotificationContent from "../notification/notificationContent";
import ButtonMenu, { ButtonMenuItem } from "../ui/buttonMenu";
import CollapseView from "../ui/collapseView";
import Tooltip from "../ui/tooltip";
import DasimetrySection from "./dasimetrySection";
import DatasetRecommendationAI from "./datasetRecommendationAI";
import SpatialHeader from "./header";
import ProjectForm from "./projectForm";
import ProjectList from "./projectList";
import SaveConfigurationPopup, {
  ConfigurationFormData
} from "./saveConfigurationPopup";
import { buildSpatialAnalysisTracker } from "./tracker";
import WeightAutoAdjustedAlert from "./weightAutoAdjustedAlert";

const TEST_ID_PREFIX = "spatial-analysis-popup";

/** Validation errors only show after the user has clicked Generate Result */
const shouldShowValidationError = (
  isGenerateResultClicked: boolean,
  message: string | null | undefined
) => isGenerateResultClicked && message !== null;

/**
 * The only weight a legacy POI dataset sends: its primary attribute ("Total
 * POI Count"), or the first weight entry when that attribute is missing.
 */
const getLegacyPoiWeightId = (
  categories: DatasetAttributeSchema[],
  firstWeightId: string | undefined
) => {
  const primaryPoiAttribute = getPrimaryPoiAttribute(categories);
  if (primaryPoiAttribute === undefined) return firstWeightId;
  return getDatasetAttributeScoringId(primaryPoiAttribute);
};

type Props = {
  onClose: (isSave?: boolean) => void;
  onOpenDatasetExplorer: (value: boolean) => void;
  onOpenDatasetTabularModal: (value: boolean) => void;
  onOpenGlobalFilter: () => void;
  onClearGlobalFilter: () => void;
};

const SpatialAnalysisPopup = ({
  onClose,
  onOpenDatasetExplorer,
  onOpenDatasetTabularModal,
  onOpenGlobalFilter,
  onClearGlobalFilter
}: Props) => {
  const { trackAction } = useDatadogAnalytics();
  const dispatch = useDispatch();
  const {
    handleResetDatasetToHistoryDataset,
    deleteAllFeatures,
    synchronizeFeatures
  } = useMap();
  const { showConfirmation } = useConfirmationModal();
  const { showToast } = useToast();
  const { handleCloseDetailView } = useHandleClearView();
  const {
    getGridConfig,
    mappingAnalysisOutput,
    mappingAreaInput,
    mappingDataset,
    uploadPendingLegendImages,
    mappingAdmAreaIds,
    mappingWidgets
  } = useConfigurationMappers();

  const [projectNameExist, setProjectNameExist] = useState(false);
  const [generateResultClicked, setGenerateResultClicked] = useState(false);

  const {
    catchmentSettings,
    adminAreaSettings,
    polygonSettings,
    currRunningAnalysisId
  } = useSelector((state: RootState) => state.spatial);
  const {
    showProjectList,
    showCatchmentPopup,
    showAreaAdminPopup,
    showPolygonPopup
  } = useSelector((state: RootState) => state.ui);
  const { datasets, weightValues, filters, rawDatasets, visualizationOrder } =
    useSelector((state: RootState) => state.dataset);
  const isWeightAutoAdjusted = useSelector(selectIsWeightAutoAdjusted);
  const weightAdjustedDatasetIds = useSelector(selectWeightAdjustedDatasetIds);
  const {
    widgets,
    visualizationOrder: widgetVisualizationOrder,
    widgetIndicates
  } = useSelector((state: RootState) => state.widget);

  const spatial = useSelector((state: RootState) => state.spatial);
  const globalFilter = useSelector((state: RootState) => state.globalFilter);
  // Accumulator-only total: it holds exactly the datasets in the analysis
  // (blocked datasets are never recorded), so no filter is needed here.
  const { isOverLimit, limit: inputRowsLimit } = useSpatialInputRowsLimit();
  // Skip the guard right after opening a project (an over-limit project must
  // stay openable); re-arms once adding a dataset clears `currentConfigId`.
  const isInputRowsOverLimit = isOverLimit && !spatial.currentConfigId;

  const { data: userProfile } = useGetUserProfileAPI();

  const {
    isAnalysisJobListEnabled,
    isGlobalFilterEnabled,
    isAdminTreeEnabled,
    isDatasetRecommendationAIEnabled,
    isBVTDataForCatchmentEnabled,
    isCoverageTypeEnabled,
    isDasimetryEnabled,
    isAdvanceDataWeightOptionsEnabled,
    isCustomMapVisualizationLegendEnabled,
    isPoiNumericAttributesEnabled,
    isAdmAreaForUserDataEnabled
  } = useAppFeatures();
  const { checkAdmAreaMappingOnGenerate } = useAdmAreaMappingCheck();

  const {
    validationErrors,
    canGenerateAnalysisResults,
    hasAvailableDataset,
    isCoverageRadiusMissing,
    isCoverageRadiusEmpty
  } = useValidationAnalysisConfigInput();

  const isAdvancedWeightMode =
    spatial.spatialConfig.scoringOption === ScoringOptionsEnum.LOG_Z_SCORE;
  // Suppress when no available dataset remains: the dataset validation error
  // already covers that state and there is nothing left to re-weigh.
  const showWeightAutoAdjustedAlert =
    isAdvanceDataWeightOptionsEnabled &&
    isWeightAutoAdjusted &&
    hasAvailableDataset;
  // A newly-added dataset is what triggers the blocking (red) advanced-mode
  // review. A composition shrink alone (baseline member removed, nothing added)
  // shows only the non-blocking tangerine reminder.
  const hasWeightAdjustedDatasets = weightAdjustedDatasetIds.length > 0;
  const isWeightAutoAdjustedBlocking =
    showWeightAutoAdjustedAlert &&
    isAdvancedWeightMode &&
    hasWeightAdjustedDatasets;
  // Datasets with a changed spatial aggregation must be refreshed first
  const hasDatasetToRefresh = Object.values(datasets).some(
    (dataset) => dataset?.properties?.hasUpdatedSpatialAggregation
  );
  // Open Project doesn't load these datasets, so they have no config to save
  // until the user refreshes or removes them
  const hasUnloadedDatasetToRefresh = Object.values(datasets).some(
    (dataset) =>
      dataset?.properties?.hasUpdatedSpatialAggregation &&
      !dataset.data.available
  );

  const { mutateSaveConfig } = useConfiguration({
    onSaveConfigSuccess: () => setShowSaveConfig(false),
    onSaveConfigError: (error) => setProjectNameExist(error.code === 409),
    onOpenProjectFromToast: () => {
      dispatch(toggleShowProjectList(true));
    }
  });

  const { mutate: postDatasetAnalysis, isPending: isLoadingPostAnalysis } =
    usePostDatasetAnalysis({
      onSuccess: (data, payload) => {
        trackAction(
          "run_spatial_analysis",
          buildSpatialAnalysisTracker(payload)
        );
        dispatch(clearAllAdmAreaMappingUpdates());

        if (isAnalysisJobListEnabled) {
          dispatch(resetUIDataConfigExceptSidebarMenu());
          if (data?.message) {
            showToast({
              type: EToastType.WARNING,
              message: (
                <NotificationContent
                  title="Queue Limit Reached"
                  description={data.message}
                />
              )
            });
          } else {
            // go to src/pages/home/index.tsx:71 to fetch analysis status
            dispatch(setCurrRunningAnalysisId(data.id));
            dispatch(setLatestAnalysisId(data.id));
            dispatch(setLocallyRunAnalysisId(data.id));
            dispatch(
              setDatasetHistory({
                datasets: datasets,
                visualizationOrder: visualizationOrder,
                rawDatasets: rawDatasets
              })
            );
            dispatch(
              setWidgetHistory({
                widgets,
                visualizationOrder: widgetVisualizationOrder,
                widgetIndicates
              })
            );
          }
        } else {
          // go to src/pages/home/index.tsx:71 to fetch analysis status
          dispatch(setCurrRunningAnalysisId(data.id));
          dispatch(setLatestAnalysisId(data.id));
          dispatch(setLocallyRunAnalysisId(data.id));
          dispatch(
            setDatasetHistory({
              datasets: datasets,
              visualizationOrder: visualizationOrder,
              rawDatasets: rawDatasets
            })
          );
        }
      },
      onError: (error, payload) => {
        trackAction(
          "run_spatial_analysis",
          buildSpatialAnalysisTracker({ ...payload, error })
        );
        showToast({
          type: EToastType.ERROR,
          message: (
            <NotificationContent
              title="Something Went Wrong"
              description="We’re sorry, we cannot proceed with your request. Please try again."
            />
          )
        });
      },
      onSettled: () => {
        dispatch(setActiveSpatialAnalysisPanel(""));
      }
    });

  const [showSaveConfig, setShowSaveConfig] = useState<boolean>(false);

  const userPermissions = useGetUserPermission();
  const aiDatasetRecommendationPermission =
    userPermissions?.[EModuleName.AI_DATASET_RECOMMENDATION]?.[
      EModulePermission.VIEW
    ];
  const [isAIPermissionDialogOpen, setIsAIPermissionDialogOpen] =
    useState(false);

  const getSaveAsMenu = (): ButtonMenuItem[] => {
    return [
      {
        name: "Existing",
        onClick: () => {
          mutateSaveConfig({
            id: spatial.currentConfigId,
            name: spatial.title ?? "",
            description: spatial.description ?? ""
          });
        },
        hide: !spatial.currentConfigId
      },
      {
        name: "New Project",
        onClick: () => {
          dispatch(setActiveSpatialAnalysisPanel(""));
          setShowSaveConfig(true);
        }
      }
    ];
  };

  const handleShowProjectList = () => {
    if (showCatchmentPopup) dispatch(toggleCatchmentPopup(false));
    if (showAreaAdminPopup) dispatch(toggleAreaAdminPopup(false));
    if (showPolygonPopup) dispatch(setShowPolygonPopup(false));
    dispatch(toggleShowProjectList(true));
  };

  const checkIsSameValue = () => {
    const { _persist, ...rest } = spatial;
    return Object.keys(rest).every(
      (key) =>
        rest[key as keyof SpatialState] ===
        spatialInitialState[key as keyof SpatialState]
    );
  };

  const hasNoAdminAreaSettings = useMemo((): boolean => {
    const levels: Array<keyof AdminAreaSettings> = [
      "lv0",
      "lv1",
      "lv2",
      "lv3",
      "lv4"
    ];
    return levels.every((level) => !adminAreaSettings?.[level]);
  }, [adminAreaSettings]);

  const hasNoDatasets = useMemo((): boolean => {
    return Object.keys(datasets || {}).length === 0;
  }, [datasets]);

  const hasNotCatchmentSettings = useMemo((): boolean => {
    const { radiusData, catchmentType, placeCoordinates, roadAccessConfig } =
      catchmentSettings || {};

    const isRadiusDataEmpty =
      !radiusData || radiusData.every((val) => val.trim() === "");
    const isPlaceCoordinatesEmpty = (placeCoordinates?.length || 0) === 0;

    return (
      isRadiusDataEmpty &&
      catchmentType === "radius" &&
      isPlaceCoordinatesEmpty &&
      roadAccessConfig?.measurementType === "distance"
    );
  }, [catchmentSettings]);

  const hasNoSpatialConfig = useMemo((): boolean => {
    const {
      areaInput,
      outputType,
      gridType,
      gridResolution,
      h3Resolution,
      scoringOption,
      areaSize,
      outputMode,
      customCatchmentRadius,
      intercept
    } = spatial.spatialConfig || {};

    return (
      areaInput === spatialInitialState.spatialConfig.areaInput &&
      outputType === spatialInitialState.spatialConfig.outputType &&
      gridType === spatialInitialState.spatialConfig.gridType &&
      gridResolution === spatialInitialState.spatialConfig.gridResolution &&
      h3Resolution === spatialInitialState.spatialConfig.h3Resolution &&
      scoringOption === spatialInitialState.spatialConfig.scoringOption &&
      areaSize === spatialInitialState.spatialConfig.areaSize &&
      outputMode === spatialInitialState.spatialConfig.outputMode &&
      customCatchmentRadius ===
        spatialInitialState.spatialConfig.customCatchmentRadius &&
      (intercept ?? "") === (spatialInitialState.spatialConfig.intercept ?? "")
    );
  }, [spatial.spatialConfig]);

  const hasNoGlobalFilter = useMemo((): boolean => {
    return (
      globalFilter.selectedAreaIds.length === 0 &&
      globalFilter.adminAreaSettings === null
    );
  }, [globalFilter.selectedAreaIds, globalFilter.adminAreaSettings]);

  const resetDisable = useMemo(() => {
    return (
      hasNoAdminAreaSettings &&
      hasNoDatasets &&
      hasNotCatchmentSettings &&
      hasNoSpatialConfig &&
      hasNoGlobalFilter
    );
  }, [
    hasNoAdminAreaSettings,
    hasNoDatasets,
    hasNotCatchmentSettings,
    hasNoSpatialConfig,
    hasNoGlobalFilter
  ]);

  const handleReset = () => {
    if (
      !Object.keys(datasets)?.length &&
      checkIsSameValue() &&
      hasNoGlobalFilter
    )
      return;

    showConfirmation({
      title: "Reset Analysis?",
      message: <div className="text-center">{MESSAGE.CONFIRMATION_RESET}</div>,
      cancelLabel: "",
      confirmLabel: "Reset",
      onConfirm: () => {
        handleResetDatasetToHistoryDataset();
        deleteAllFeatures();
        dispatch(resetSpatialState());
        dispatch(resetDatasetState());
        dispatch(resetWidgetState());
        handleCloseDetailView();
        setGenerateResultClicked(false);
        dispatch(resetGlobalFilter());
        dispatch(toggleAreaAdminPopup(false));
        dispatch(toggleGlobalFilterPopup(false));
      }
    });
  };

  const handleGoBack = () => {
    dispatch(toggleShowProjectList(false));
    // reset query cache to refetch project list from page 1
    queryClient.removeQueries({
      queryKey: ["config-list-infinite"]
    });
  };

  const handleSaveConfiguration = (data: ConfigurationFormData) => {
    mutateSaveConfig(data);
    handleChangeConfigInfo({
      id: data.id,
      projectDescription: data.description,
      projectName: data.name
    });
  };

  const convertMarkerToWkt = (
    marker: MarkerPinItem,
    index: number
  ): string[] => {
    try {
      if (catchmentSettings?.catchmentType === ECatchmentType.BY_RADIUS) {
        return geoJSONArrayToWKT(
          (catchmentSettings?.radiusData || []).map((radius) =>
            getRadiusPolygon([marker.lng, marker.lat], Number(radius) || 0)
          )
        );
      }

      return geoJSONArrayToWKT(
        (catchmentSettings?.roadAccessConfig?.isochroneData as IsochroneData[])[
          index
        ].data.flatMap((d) => d.features)
      );
    } catch {
      return [];
    }
  };

  const buildPolygonInputData = (): CustomCatchment[] =>
    Object.values(polygonSettings).map((polygonSetting, index) => ({
      name: polygonSetting.name || `polygon-${index}`,
      polygon: [geoJSONToWKT(polygonSetting.polygon) as string]
    }));

  const getInputData = (): Marker[] | AdminArea | CustomCatchment[] => {
    if (spatial.spatialConfig.areaInput?.value === AreaInputType.ADMIN_AREA) {
      if (isAdminTreeEnabled) {
        return {
          adm_area_ids: spatial.adminTreeSettings.selectedAreaIds
        };
      }

      // Get the lowest admin level from adminAreaSettings
      const lowestAdminLevel = Object.entries(adminAreaSettings || {})
        .filter(([key, value]) => key.startsWith("lv") && value)
        .sort(([keyA], [keyB]) => {
          // Sort by level number in descending order (lv5 before lv4, etc.)
          const levelA = Number.parseInt(keyA.substring(2));
          const levelB = Number.parseInt(keyB.substring(2));
          return levelB - levelA;
        })
        .map(([_, value]) => value)[0];

      return { id: lowestAdminLevel?.value || "" };
    } else if (
      spatial.spatialConfig.areaInput?.value === AreaInputType.POLYGON
    ) {
      return buildPolygonInputData();
    }

    if (catchmentSettings?.catchmentInputBy === ECatchmentInputBy.BY_LOCATION) {
      return (
        catchmentSettings?.placeCoordinates?.map((marker, index) => {
          return {
            name: marker.locationName || `Marker #${index + 1}`,
            coordinate: {
              latitude: marker.lat,
              longitude: marker.lng
            },
            polygon: convertMarkerToWkt(marker, index)
          };
        }) || []
      );
    } else if (
      isBVTDataForCatchmentEnabled &&
      catchmentSettings?.catchmentInputBy === ECatchmentInputBy.BY_POLYGON
    ) {
      return buildPolygonInputData();
    } else {
      const datasets = catchmentSettings?.datasetConfig?.datasets ?? {};

      // Polygon-typed catchment datasets follow the same Marker[] shape as
      // POI catchment dataset (input field) — each polygon row → Marker with
      // centroid coordinate + polygon WKT. MultiPolygon rows are preserved
      // as a single Marker carrying a MultiPolygon WKT.
      if (isBVTDataForCatchmentEnabled) {
        const polygonEntries = Object.values(datasets).filter(
          (entry) => entry.dataset?.data?.spatial_aggregation_type === "polygon"
        );
        if (polygonEntries.length > 0) {
          return polygonEntries
            .flatMap((entry) =>
              convertDatasetResponseToFeatureCollection(
                entry.rawDataset
              ).features.flatMap((feature) => {
                const geom = feature.geometry;
                if (geom?.type !== "Polygon" && geom?.type !== "MultiPolygon") {
                  return [];
                }
                const wkt = geoJSONToWKT(geom);
                if (!wkt) return [];
                const [lng, lat] = pointOnFeature(geom).geometry.coordinates;
                return [
                  {
                    coordinate: { latitude: lat, longitude: lng },
                    polygon: [wkt]
                  }
                ];
              })
            )
            .map((marker, i) => ({
              ...marker,
              name: `Location layer polygon #${i + 1}`
            }));
        }
      }

      const markers: MarkerPinItem[] = [];

      createMarkersFromDataset(
        Object.values(datasets),
        (label, coordinates) => {
          markers.push({
            id: uuidv4(),
            locationName: label,
            lat: coordinates[1],
            lng: coordinates[0]
          });
        }
      );

      return (
        markers?.map((marker, index) => {
          return {
            name: marker.locationName || `Marker #${index + 1}`,
            coordinate: {
              latitude: marker.lat,
              longitude: marker.lng
            },
            polygon: convertMarkerToWkt(marker, index)
          };
        }) || []
      );
    }
  };

  const datasetFilter = (
    filters: Record<string, DatasetFilter[]>,
    timezone?: Timezone
  ): Record<string, SpatialAnalysisDatasetFilter[]> => {
    const result: Record<string, SpatialAnalysisDatasetFilter[]> = {};

    for (const [datasetId, filterArray] of Object.entries(filters)) {
      const transformedFilters: SpatialAnalysisDatasetFilter[] = [];

      const filterTransformed = getDeepestAdminFilterFirst(filterArray);

      for (const filter of filterTransformed) {
        const {
          filterId: filterIdTemp,
          attributeCode = "",
          paired_key_attribute_id
        } = filter;
        const filterId = removeStartFromUnderscore(
          paired_key_attribute_id ?? filterIdTemp
        );

        transformedFilters.push({
          schema_id: filterId,
          filter: buildFilterAsDatasetPayload(filter, timezone),
          attribute_code: attributeCode
        });
      }
      result[datasetId] = transformedFilters;
    }
    return result;
  };

  const getDatasetAvailableStatus = async () => {
    const datasetIds = Object.keys(datasets);

    const datasetMetadata = await Promise.all(
      datasetIds.map(async (id) => {
        const baseId = id.includes("_") ? id.split("_")[0] : id;
        try {
          const res = await getDatasetMetadata(baseId);
          const published = res.published ?? false;
          // An unpublished dataset cannot be used even after a refresh, so it
          // shows as unavailable instead of asking for a refresh
          if (!published) {
            dispatch(
              updateDatasetPublishedStatus({ datasetId: id, published: false })
            );
            dispatch(
              setDatasetHasUpdatedSpatialAggregation({
                datasetId: id,
                hasUpdated: false
              })
            );
            return false;
          }
          const isAdmAreaMappingUpdated = await checkAdmAreaMappingOnGenerate(
            id,
            res
          );
          // The published status is left as is so the dataset keeps its
          // current data on the map until the user refreshes it
          if (datasets[id].properties.hasUpdatedSpatialAggregation) {
            return false;
          }
          if (
            isAdmAreaForUserDataEnabled &&
            hasSpatialAggregationChanged(
              datasets[id].data.spatial_aggregation_type,
              res
            )
          ) {
            dispatch(
              setDatasetHasUpdatedSpatialAggregation({
                datasetId: id,
                hasUpdated: true
              })
            );
            return false;
          }
          dispatch(updateDatasetPublishedStatus({ datasetId: id, published }));
          return !isAdmAreaMappingUpdated;
        } catch (err) {
          console.error(err);
          dispatch(
            updateDatasetPublishedStatus({ datasetId: id, published: false })
          );
          return false;
        }
      })
    );

    return datasetMetadata;
  };

  const handleRunAnalysis = async () => {
    setGenerateResultClicked(true);

    // Block when the combined input rows across datasets exceed the limit.
    if (isInputRowsOverLimit) return;

    // Advanced-mode weights were auto-adjusted after the last save; the user
    // must review and re-save Data Weight settings before generating.
    if (isWeightAutoAdjustedBlocking) return;

    const datasetAvailableStatus = (await getDatasetAvailableStatus()).some(
      (status) => !status
    );

    if (!canGenerateAnalysisResults || datasetAvailableStatus) return;

    const datasetFilterTransformed = datasetFilter(
      filters,
      userProfile?.timezone
    );

    /** Single signal: advanced normalization is persisted as LOG_Z_SCORE on the
     * scoring option (set when the user saves in advanced mode). That flag — not
     * any z_score inspection — flips the wire to LOG_Z_SCORE + z_score.
     */
    const isAdvancedNormalization =
      spatial.spatialConfig.scoringOption === ScoringOptionsEnum.LOG_Z_SCORE;

    const datasetKeys = Object.keys(datasets);

    const formattedDatasets = datasetKeys.map((id) => {
      const datasetBaseId = id.includes("_") ? id.split("_")[0] : id;
      const currentDataset = datasets[id];
      const currentDatasetDisplayName =
        currentDataset.data.display_name || currentDataset.data.name;
      const datasetWeightAttributes = weightValues[id]?.attributes ?? {};
      const datasetIncludeScoring = weightValues[id]?.include_scoring !== false;
      const isPoiDataset = currentDataset.properties.spatialType === "poi";

      const buildWeightPayload = (
        schemaId: string,
        value: (typeof datasetWeightAttributes)[string] | undefined,
        includeScoring: boolean
      ) => {
        const negativeImpact = getNegativeImpactValue(value);
        const percentage = getPercentageValue(value);
        const resolvedWeightPrimitive = percentage ?? value;
        const parsedWeight = parseWeightValue(resolvedWeightPrimitive);
        /** z_score only under advanced normalization, and only for included
         * weights (BE: an excluded weight needs no log_mean/log_std_dev).
         */
        const zScore =
          isAdvancedNormalization && includeScoring
            ? parseZScore(value)
            : undefined;

        return {
          schema_id: schemaId,
          weight: parsedWeight,
          include_scoring: includeScoring,
          ...(negativeImpact && {
            negative_impact: negativeImpact
          }),
          ...(zScore && { z_score: zScore })
        };
      };

      // Legacy POI run-analysis payloads send only the scored "Total POI Count"
      // attribute. Configuration payloads still keep the other POI attributes
      // for restoration, but the analysis request omits them entirely. Once
      // numeric attributes are enabled a POI dataset sends every weight entry,
      // exactly like any other dataset.
      const allWeightEntries = Object.entries(datasetWeightAttributes);
      const isLegacyPoiDataset = isPoiDataset && !isPoiNumericAttributesEnabled;
      const primaryPoiAttributeId = isLegacyPoiDataset
        ? getLegacyPoiWeightId(
            currentDataset.categories,
            allWeightEntries[0]?.[0]
          )
        : undefined;
      const weightEntries = isLegacyPoiDataset
        ? allWeightEntries.filter(
            ([schemaId]) => schemaId === primaryPoiAttributeId
          )
        : allWeightEntries;

      const weight = weightEntries.map(([schemaId, value]) =>
        buildWeightPayload(
          schemaId,
          value,
          isLegacyPoiDataset
            ? datasetIncludeScoring
            : datasetIncludeScoring && getIsIncludedScoring(value)
        )
      );

      return {
        id: datasetBaseId,
        filter: datasetFilterTransformed[id] ?? [],
        weight,
        display_name: currentDatasetDisplayName,
        grid_config: getGridConfig(currentDataset),
        include_scoring: datasetIncludeScoring,
        ...(currentDataset.group_by !== "none" && {
          group_by: currentDataset.group_by
        })
      };
    });

    // Order datasets by `visualizationOrder` (matched on the original key, which
    // keeps the `_` suffix). Unlisted datasets rank just past the listed range,
    // offset by their original index, so they land at the end in their original
    // order.
    const orderRank = (key: string, originalIndex: number) => {
      const index = visualizationOrder.indexOf(key);
      return index === -1 ? visualizationOrder.length + originalIndex : index;
    };

    const orderedDatasets = datasetKeys
      .map((key, index) => ({ key, index }))
      .sort((a, b) => orderRank(a.key, a.index) - orderRank(b.key, b.index))
      .map(({ index }) => formattedDatasets[index]);

    // Upload any pending legend marker images first, then persist
    // their asset ids into the dataset config. A failed upload aborts the
    // run so the config is never persisted with rows missing asset ids.
    let assetIdByImageUrl = new Map<string, string>();
    if (isCustomMapVisualizationLegendEnabled) {
      try {
        assetIdByImageUrl = await uploadPendingLegendImages();
      } catch {
        showToast({
          type: EToastType.ERROR,
          message: MESSAGE.CUSTOM_LEGEND_UPLOAD_ERROR_RUN_ANALYSIS
        });
        return;
      }
    }
    const configuration = {
      id: spatial.currentConfigId,
      name: spatial.title ?? "",
      description: spatial.description ?? "",
      adm_area_ids: mappingAdmAreaIds(),
      datasets: mappingDataset(assetIdByImageUrl),
      area_input: mappingAreaInput(),
      analysis_output: mappingAnalysisOutput(),
      widget_config: { widgets: mappingWidgets() }
    };

    const analysisConfig = {
      datasets: orderedDatasets,
      input: getInputData(),
      output: getOutputConfiguration(isAdvancedNormalization),
      configuration: JSON.stringify(configuration)
    };

    handleCloseDetailView();
    postDatasetAnalysis(analysisConfig);
  };

  const getOutputConfiguration = (
    isAdvancedNormalization: boolean
  ): OutputSiteProfiling | OutputGrid => {
    const {
      outputType,
      gridType,
      gridResolution,
      h3Resolution,
      scoringOption,
      outputMode,
      customCatchmentRadius,
      intercept,
      dasimetry
    } = spatial.spatialConfig;

    /** Advanced normalization is sent as LOG_Z_SCORE; the intercept rides along
     * only when the (optional) field is filled.
     */
    const resolvedScoringOption = isAdvancedNormalization
      ? ScoringOptionsEnum.LOG_Z_SCORE
      : (scoringOption as ScoringOptions);
    const outputZScore =
      isAdvancedNormalization && intercept?.trim()
        ? { z_score: { intercept: parseFormattedNumber(intercept) } }
        : {};

    if (outputType === EOutputAnalysisType.GRID) {
      const isGeohash = gridType === EOutputGridType.GEOHASH;
      const resolutionValue = isGeohash
        ? Number(gridResolution?.value)
        : Number(h3Resolution?.value);

      return {
        output_type: AnalysisResultType.TYPE_GRID,
        grid_config: {
          grid_type: isGeohash
            ? AnalysisResultType.TYPE_GEOHASH
            : AnalysisResultType.TYPE_H3,
          level: resolutionValue,
          ...buildCoverageTypePayload({
            isEnabled: isCoverageTypeEnabled,
            outputMode,
            radius: customCatchmentRadius ?? 0,
            type: ECatchmentType.BY_RADIUS.toUpperCase()
          })
        },
        scoring_option: resolvedScoringOption,
        ...outputZScore,
        ...(isDasimetryEnabled &&
          dasimetry && { proportion_method: ProportionMethodOutput.DASYMETRIC })
      };
    }

    return {
      output_type: AnalysisResultType.TYPE_SITE_PROFILING,
      scoring_option: resolvedScoringOption,
      ...outputZScore,
      ...(isDasimetryEnabled &&
        dasimetry && { proportion_method: ProportionMethodOutput.DASYMETRIC })
    };
  };

  const isOutputValidationError = shouldShowValidationError(
    generateResultClicked,
    validationErrors?.outputAnalysisMessage
  );

  const isResolutionValidationError = shouldShowValidationError(
    generateResultClicked,
    validationErrors?.resolutionMessage
  );

  // The refresh warning already tells the user what to do with the dataset
  const isDatasetValidationError =
    shouldShowValidationError(
      generateResultClicked,
      validationErrors?.datasetMessage
    ) && !hasDatasetToRefresh;

  const handleChangeConfigInfo = (data: ProjectFormData) => {
    if (
      data.projectName === spatial.title &&
      data.projectDescription === spatial.description &&
      data.id === spatial.currentConfigId
    )
      return;
    dispatch(
      setSpatialState({
        ...spatial,
        currentConfigId: data.id,
        title: data.projectName,
        description: data.projectDescription
      })
    );
  };

  return (
    <div
      className={twMerge(
        "absolute top-[30px] left-[16px] z-[9] border-gainsboro bg-white rounded-lg w-[338px] max-h-[calc(100vh-54px)] overflow-hidden",
        "flex flex-col",
        Object.keys(datasets).length > 0 ? "" : "min-h-[224px]"
      )}
    >
      {showProjectList ? (
        <ProjectList onGoBack={handleGoBack} />
      ) : (
        <>
          {/* Header */}
          <SpatialHeader title="Analysis" onClose={onClose}>
            <div className="flex gap-3.5">
              <Tooltip content="Reset Analysis" position="bottom-center">
                <button
                  type="button"
                  data-testid="btn-reset"
                  className={cn(
                    "bg-transparent border-0 p-0 transition-colors",
                    {
                      "cursor-pointer fill-dim-gray": !resetDisable,
                      "cursor-not-allowed fill-light-gray": resetDisable
                    }
                  )}
                  onClick={handleReset}
                  disabled={resetDisable}
                  aria-disabled={resetDisable}
                  aria-label="Reset Analysis"
                >
                  <FilePlusIcon />
                </button>
              </Tooltip>
              <Tooltip content="Open Project" position="bottom-center">
                <button
                  type="button"
                  className="bg-transparent border-0 p-0"
                  onClick={handleShowProjectList}
                  aria-label="Open Project"
                >
                  <FolderOpenIcon className="text-dim-gray" />
                </button>
              </Tooltip>
            </div>
          </SpatialHeader>
          {/* Content */}
          <div className="flex flex-col mt-3 custom-scrollbar overflow-y-auto pb-[64px]">
            {/* Project and description */}
            {spatial.currentConfigId && (
              <ProjectForm
                initialData={{
                  id: spatial.currentConfigId,
                  projectName: spatial.title ?? "",
                  projectDescription: spatial.description ?? ""
                }}
                onUpdate={handleChangeConfigInfo}
              />
            )}

            {/* Filter Area | Global Filter*/}
            {isGlobalFilterEnabled && (
              <>
                <GlobalFilter
                  onClearFilter={onClearGlobalFilter}
                  onOpenFilter={onOpenGlobalFilter}
                />
                <Divider className="bg-white-smoke border-none w-[100%] min-h-[8px] my-4 [&>div]:border-0" />
              </>
            )}

            {/* Data selection */}
            <div
              className="flex flex-col px-4"
              data-tourid="dataset-added-panel"
            >
              <span className="font-sans font-semibold text-txtHeading text-sm">
                Data Selection
              </span>
              <p className=" text-txtBody font-sans text-[10px]">
                Select your own data or explore our dataset library.
              </p>

              {isInputRowsOverLimit && (
                <LimitWarning
                  className="mt-3"
                  title={MESSAGE.LIMIT_THRESHOLD_WARNING.TITLE}
                  message={MESSAGE.LIMIT_THRESHOLD_WARNING.MESSAGE(
                    inputRowsLimit,
                    "dataset"
                  )}
                />
              )}

              <DatasetList
                isValidationError={isDatasetValidationError}
                onOpenDatasetTabularModal={() =>
                  onOpenDatasetTabularModal(true)
                }
              />
              {isDatasetValidationError && validationErrors?.datasetMessage && (
                <p className="px-2 text-[10px] text-cinnabar self-start">
                  {validationErrors.datasetMessage}
                </p>
              )}

              <div className="flex flex-row items-center gap-2 mt-3 ">
                <Button
                  variant="secondary"
                  disabled={isInputRowsOverLimit}
                  className="h-8 btn-secondary text-sm !font-bold flex-grow flex-shrink-0 disabled:cursor-not-allowed disabled:text-neutral-200 disabled:hover:bg-transparent"
                  onClick={() => onOpenDatasetExplorer(true)}
                  data-testid={`${TEST_ID_PREFIX}-add-dataset-button`}
                >
                  <CirclePlusIcon
                    className={twMerge(
                      "fill-teal-500",
                      isInputRowsOverLimit && "fill-gray-300"
                    )}
                  />
                  <span>Add Dataset</span>
                </Button>

                {isDatasetRecommendationAIEnabled && (
                  <LokasiAIProvider
                    httpClient={axiosInstance}
                    topics={["dataset_recommendation"]}
                  >
                    <DatasetRecommendationAI
                      onClickDisabled={
                        aiDatasetRecommendationPermission
                          ? undefined
                          : () => setIsAIPermissionDialogOpen(true)
                      }
                    />
                  </LokasiAIProvider>
                )}
              </div>
            </div>

            <Divider className="bg-white-smoke border-none w-[100%] min-h-[8px] mt-4 [&>div]:border-0" />
            <CollapseView title="Spatial Settings">
              {/* Area Input */}
              <SpatialAreaInput
                onOpenCatchmentPopup={() => {
                  // Sync draft from committed state on user-initiated open.
                  // Reopen to the selected tab at catchment popup
                  // reducer (not this handler), so the user's draft is preserved
                  // across that round trip.
                  dispatch(setCatchmentSettingsTemp(catchmentSettings));
                  dispatch(setActiveSpatialAnalysisPanel("showCatchmentPopup"));
                  if (isBVTDataForCatchmentEnabled) {
                    dispatch(setPolygonSettingsTemp(polygonSettings));
                  }
                }}
                onOpenAreaAdminPopup={() => {
                  dispatch(setActiveSpatialAnalysisPanel("showAreaAdminPopup"));
                  if (isAdminTreeEnabled) {
                    dispatch(
                      setAdminTreeSettingsTempSelectedIds(
                        spatial.adminTreeSettings.selectedAreaIds
                      )
                    );
                    dispatch(
                      setAdminAreaSettingsDataTemp(
                        spatial.adminAreaSettingsData
                      )
                    );
                  }
                }}
                onOpenPolygonPopup={() => {
                  synchronizeFeatures();
                  dispatch(setPolygonSettingsTemp(polygonSettings));
                  dispatch(setActiveSpatialAnalysisPanel("showPolygonPopup"));
                }}
                isError={
                  generateResultClicked && !!validationErrors?.areaInputMessage
                }
              />
              {generateResultClicked && validationErrors?.areaInputMessage && (
                <p className="px-4 text-[10px] text-cinnabar self-start my-1">
                  {validationErrors.areaInputMessage}
                </p>
              )}
              {/* Output analysis */}
              <SpatialOutputAnalysis
                isOutputValidationError={isOutputValidationError}
                isResolutionValidationError={isResolutionValidationError}
              />
              <div className="flex flex-col gap-4 px-4 my-2">
                <Button
                  variant="secondary"
                  className="h-[32px] w-full btn-secondary text-sm !font-bold"
                  onClick={() =>
                    dispatch(
                      setActiveSpatialAnalysisPanel(
                        "showDataWeightSettingsPopup"
                      )
                    )
                  }
                >
                  {spatial.spatialConfig.outputType ===
                  EOutputAnalysisType.GRID ? (
                    <span>Setting Data Weight</span>
                  ) : (
                    <span>Set Data Weight</span>
                  )}
                </Button>
                {showWeightAutoAdjustedAlert && (
                  <WeightAutoAdjustedAlert
                    isAdvancedMode={
                      isAdvancedWeightMode && hasWeightAdjustedDatasets
                    }
                    data-testid={`${TEST_ID_PREFIX}-weight-auto-adjusted-alert`}
                  />
                )}
              </div>
              {isDasimetryEnabled && <DasimetrySection />}
            </CollapseView>
            <Divider className="bg-white-smoke w-[100%] min-h-[8px] [&>div]:border-0" />
          </div>
          {/* Bottom button config */}
          <div className="flex px-4 gap-2 absolute bottom-0 bg-white w-full py-3 rounded-t-none rounded-lg">
            <ButtonMenu
              variant="secondary"
              title="Save as"
              className="w-1/2 !font-bold disabled:cursor-not-allowed  disabled:hover:bg-none"
              menu={getSaveAsMenu()}
              disabled={
                !hasAvailableDataset ||
                hasUnloadedDatasetToRefresh ||
                isInputRowsOverLimit ||
                isCoverageRadiusEmpty
              }
              data-testid={`${TEST_ID_PREFIX}-save-as-button`}
            />
            <Button
              className="h-[40px] w-1/2 btn-primary text-sm !font-bold disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Generate Results"
              disabled={
                isWeightAutoAdjustedBlocking ||
                hasDatasetToRefresh ||
                isInputRowsOverLimit ||
                isCoverageRadiusMissing ||
                (isAnalysisJobListEnabled
                  ? isLoadingPostAnalysis
                  : !!currRunningAnalysisId)
              }
              onClick={handleRunAnalysis}
              data-testid={`${TEST_ID_PREFIX}-generate-results-button`}
              data-tourid="explore-finish-marker"
            >
              <span>Generate Results</span>
            </Button>
          </div>
        </>
      )}
      {showSaveConfig && (
        <SaveConfigurationPopup
          show
          nameIsExist={projectNameExist}
          setNameIsExist={setProjectNameExist}
          onClose={() => setShowSaveConfig(false)}
          onSave={handleSaveConfiguration}
        />
      )}
      {isAIPermissionDialogOpen &&
        !aiDatasetRecommendationPermission &&
        isDatasetRecommendationAIEnabled &&
        createPortal(
          <UnlockPremiumModal
            title={MESSAGE.UNLOCK_AI_POPOVER_TITLE}
            description={MESSAGE.UNLOCK_AI_CONTENT_POPOVER_DESCRIPTION}
            contactUsDesc={
              MESSAGE.UNLOCK_AI_CONTENT_POPOVER_DESCRIPTION_CONTENT
            }
            onClose={() => setIsAIPermissionDialogOpen(false)}
            classNameContainer="z-[9999]"
          />,
          document.body
        )}
    </div>
  );
};

export default SpatialAnalysisPopup;
